/**
 * The ONE query-key factory (rules/01 §3). The internal portal has no tenant scope: every operator read is
 * platform-wide, so keys start with ["platform", …] and are dropped wholesale on sign-in and sign-out. The
 * `session` keys are the operator's own identity. A guard test asserts every factory keeps this shape.
 */
import type { paths } from "./schema";

export interface TenantListFilters {
  q?: string;
  status?: string;
  page: number;
  size: number;
}

export interface FleetFilters {
  status?: string;
  page: number;
  size: number;
}

/** Audit timeline filter; pages are keyed by `before` inside the infinite query, not here. */
export interface PlatformAuditFilters {
  tenantId?: string;
  action?: string;
  limit: number;
}

/** The backlog view's filters (M20 I1-04); the cursor pages inside the infinite query, not here. */
export interface SmsMessageFilters {
  tenantId?: string;
  status?: string;
  limit: number;
}

/** M26 (route 40): the rule-set list's filters, as the generated query type spells them (scheme, jurisdiction, status). */
export type PayrollRuleSetFilters = NonNullable<
  paths["/api/v1/internal/payroll/rule-sets"]["get"]["parameters"]["query"]
>;

export const queryKeys = {
  session: {
    /** The operator behind the `nacs_operator` cookie. */
    operator: () => ["session", "operator"] as const,
    /** The person behind the customer-portal cookie, checked by the login page before asking for a code. */
    account: () => ["session", "account"] as const,
  },
  platform: {
    all: () => ["platform"] as const,
    mobileReleases: {
      all: () => ["platform", "mobileReleases"] as const,
      settings: () => ["platform", "mobileReleases", "settings"] as const,
    },
    tenants: {
      all: () => ["platform", "tenants"] as const,
      list: (filters: TenantListFilters) => ["platform", "tenants", "list", filters] as const,
      /** The whole catalogue for pickers (audit filter): name and id only matter. */
      options: () => ["platform", "tenants", "options"] as const,
      detail: (tenantId: string) => ["platform", "tenants", "detail", tenantId] as const,
    },
    fleet: {
      all: () => ["platform", "fleet"] as const,
      list: (filters: FleetFilters) => ["platform", "fleet", "list", filters] as const,
      unclaimed: () => ["platform", "fleet", "unclaimed"] as const,
    },
    /** M9 §6 device console: the catalogue and this operator's own history, both per terminal. */
    console: {
      all: () => ["platform", "console"] as const,
      commands: (deviceId: string) => ["platform", "console", "commands", deviceId] as const,
      history: (deviceId: string) => ["platform", "console", "history", deviceId] as const,
    },
    /** M11 (ADR-0015 §5): the catalogue editor — items with their price history, and the discount rules. */
    catalogue: {
      all: () => ["platform", "catalogue"] as const,
      list: () => ["platform", "catalogue", "list"] as const,
    },
    /** M14 follow-up (ADR-0007 addendum 2026-09-15): the device_models capacity editor. */
    deviceModels: {
      all: () => ["platform", "deviceModels"] as const,
      list: () => ["platform", "deviceModels", "list"] as const,
    },
    /** M11: money across organisations — the overview tiles and the newest orders. */
    billing: {
      all: () => ["platform", "billing"] as const,
      overview: () => ["platform", "billing", "overview"] as const,
    },
    /** M20 (ADR-0024): the SMS platform settings, the templates registry, the backlog and one organisation's wallet. */
    sms: {
      all: () => ["platform", "sms"] as const,
      settings: () => ["platform", "sms", "settings"] as const,
      templates: () => ["platform", "sms", "templates"] as const,
      overview: () => ["platform", "sms", "overview"] as const,
      messages: (filters: SmsMessageFilters) => ["platform", "sms", "messages", filters] as const,
      tenant: (tenantId: string) => ["platform", "sms", "tenant", tenantId] as const,
    },
    /** M24 (ADR-0026): one organisation's Face Kiosk seats on the tenant page. */
    faceKiosk: {
      all: () => ["platform", "faceKiosk"] as const,
      tenant: (tenantId: string) => ["platform", "faceKiosk", "tenant", tenantId] as const,
    },
    /** M25 (ADR-0027): one organisation's Labour Attendance — item, active labourers and sites — on the tenant page. */
    labour: {
      all: () => ["platform", "labour"] as const,
      tenant: (tenantId: string) => ["platform", "labour", "tenant", tenantId] as const,
    },
    /** M27 (ADR-0030 addendum, contracts §10 route 57): one organisation's Rooms and Equipment counts on the tenant page. */
    rooms: {
      all: () => ["platform", "rooms"] as const,
      tenant: (tenantId: string) => ["platform", "rooms", "tenant", tenantId] as const,
    },
    /**
     * M26 (ADR-0031, contracts §22): Payroll law — the platform's rule sets and tax years and the readiness list.
     * A detail page finds its row in the list read (there is no `GET …/:id`), so `ruleSet(id)` and `taxYear(id)`
     * key a page's own derived reads (a preview), never a second fetch of the row.
     */
    payrollLaw: {
      all: () => ["platform", "payrollLaw"] as const,
      ruleSets: (filters: PayrollRuleSetFilters) => ["platform", "payrollLaw", "ruleSets", filters] as const,
      ruleSet: (id: string) => ["platform", "payrollLaw", "ruleSet", id] as const,
      taxYears: () => ["platform", "payrollLaw", "taxYears"] as const,
      taxYear: (id: string) => ["platform", "payrollLaw", "taxYear", id] as const,
      readiness: (month: string) => ["platform", "payrollLaw", "readiness", month] as const,
    },
    /** M26: one organisation's Payroll item and run state (route 112) on the tenant page. */
    payroll: {
      all: () => ["platform", "payroll"] as const,
      tenant: (tenantId: string) => ["platform", "payroll", "tenant", tenantId] as const,
    },
    /** M21 (ADR-0028 OQ 1, route 138): one organisation's Co-working item and counts on the tenant page. */
    coworking: {
      all: () => ["platform", "coworking"] as const,
      tenant: (tenantId: string) => ["platform", "coworking", "tenant", tenantId] as const,
    },
    queues: {
      all: () => ["platform", "queues"] as const,
      summaries: () => ["platform", "queues", "summaries"] as const,
    },
    audit: {
      all: () => ["platform", "audit"] as const,
      list: (filters: PlatformAuditFilters) => ["platform", "audit", "list", filters] as const,
    },
    operators: {
      all: () => ["platform", "operators"] as const,
      list: () => ["platform", "operators", "list"] as const,
    },
    reviewers: {
      all: () => ["platform", "reviewers"] as const,
      list: () => ["platform", "reviewers", "list"] as const,
    },
  },
} as const;
